import { forwardRef, useRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/shared/lib/cn';

export interface DateInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  /** Data no formato ISO `YYYY-MM-DD` ('' quando vazia). */
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
}

/** `YYYY-MM-DD` → `DD/MM/AAAA`; vazio mostra o placeholder do Figma. */
function formatBr(value: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : null;
}

/**
 * Campo de data (Figma FGP: Text field com icon/calendar e "00/00/0000").
 * O `<input type="date">` nativo fica sobre o campo (transparente): mantém teclado, validação e o
 * seletor do navegador, enquanto a aparência segue o Figma.
 */
export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(function DateInput(
  { value, onChange, invalid, className, disabled, ...props },
  forwardedRef,
) {
  const innerRef = useRef<HTMLInputElement | null>(null);
  const formatted = formatBr(value);

  return (
    <div
      className={cn(
        'relative flex h-10 w-full items-center gap-1 rounded-xl border border-line bg-surface px-2',
        'focus-within:border-brand-outline focus-within:ring-2 focus-within:ring-brand-100',
        disabled && 'bg-surface-muted',
        className,
        invalid && 'border-flag-danger focus-within:border-flag-danger focus-within:ring-red-100',
      )}
    >
      <img src="/icons/calendar.svg" alt="" className="size-6 shrink-0" />
      <span
        aria-hidden="true"
        className={cn('text-trim truncate text-base leading-none', formatted ? 'text-ink' : 'text-placeholder')}
      >
        {formatted ?? '00/00/0000'}
      </span>
      <input
        ref={(node) => {
          innerRef.current = node;
          if (typeof forwardedRef === 'function') forwardedRef(node);
          else if (forwardedRef) forwardedRef.current = node;
        }}
        type="date"
        lang="pt-BR"
        value={value}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        onChange={(event) => onChange(event.target.value)}
        onClick={() => {
          // Abre o calendário ao clicar em qualquer ponto do campo (onde suportado).
          try {
            innerRef.current?.showPicker?.();
          } catch {
            // Alguns navegadores bloqueiam showPicker fora de gesto do usuário; o input segue editável.
          }
        }}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed [&::-webkit-calendar-picker-indicator]:hidden"
        {...props}
      />
    </div>
  );
});
